import { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase/firebase";
import useAuth from "./useAuth";

const formatStatus = (raw) => {
  if (raw === "completed" || raw === "complete") return "Complete";
  if (raw === "in_progress") return "In Progress";
  return "Not Attempted";
};

const safeDateString = (ts) => {
  try {
    return ts?.toDate?.()?.toLocaleDateString?.() ?? "";
  } catch {
    return "";
  }
};

const getSortableMs = (ts) => {
  try {
    return ts?.toDate?.()?.getTime?.() ?? 0;
  } catch {
    return 0;
  }
};

const tallyDiagnostic = (responses) => {
  if (!responses || typeof responses !== "object")
    return { correct: 0, incorrect: 0 };

  let correct = 0;
  let incorrect = 0;

  for (const k of Object.keys(responses)) {
    const r = responses[k];
    if (!r) continue;
    if (r.isCorrect === true) correct += 1;
    else if (r.isCorrect === false) incorrect += 1;
  }

  return { correct, incorrect };
};

export default function useStudentAssignments() {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) {
      setAssignments([]);
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(true);

    const loadAssignments = async () => {
      try {
        const q = query(
          collection(db, "users", user.uid, "assignments"),
          orderBy("assignedAt", "desc")
        );

        const snap = await getDocs(q);

        return snap.docs.map((d) => {
          const data = d.data();

          return {
            id: d.id,

            // display fields used by AssignmentCard
            title: data.packetName,
            category: data.section,
            subtitle: `${data.questionCount} Problems`,
            assignedDate: safeDateString(data.assignedAt),
            status:
              data.status === "completed"
                ? "Complete"
                : data.status === "in_progress"
                ? "In Progress"
                : "Not Attempted",
            correct: data.correct ?? 0,
            incorrect: data.incorrect ?? 0,
            estimatedMinutes: data.estimatedMinutes ?? null,
            calculatorAllowed: data.calculatorAllowed ?? false,

            // keep existing identifiers (these often drive routing)
            packetId: data.packetId ?? data.packetId === "" ? data.packetId : data.packetId, // harmless
            packetName: data.packetName,
            testId: data.packetId, // backward compat with your current mapping
            section: data.section,

            // internal
            _sortMs: getSortableMs(data.assignedAt),
            isDiagnostic: false,
          };
        });
      } catch (err) {
        console.error("Failed to load student assignments:", err);
        return [];
      }
    };

    const loadDiagnostics = async () => {
      // IMPORTANT: diagnostics are optional; failures must not break assignments.
      try {
        const snap = await getDocs(
          collection(db, "users", user.uid, "diagnosticAttempts")
        );

        const base = snap.docs.map((d) => {
          const data = d.data();
          const counts = tallyDiagnostic(data.responses);

          const diagnosticId = data.diagnosticId ?? d.id;
          const section = data.section ?? "Diagnostic";

          // default display values
          const title = data.name || `${section} Diagnostic`;
          const administeredCount = data.administeredCount ?? 36;

          return {
            id: `diag_${diagnosticId}`,

            // display fields used by AssignmentCard
            title,
            category: section,
            subtitle: `${administeredCount} Questions`,
            assignedDate: safeDateString(data.createdAt),
            status: formatStatus(data.status),
            correct: counts.correct,
            incorrect: counts.incorrect,
            estimatedMinutes: data.estimatedMinutes ?? null,
            calculatorAllowed: false,

            // make it feel like an assignment
            packetId: diagnosticId,
            packetName: title,
            testId: diagnosticId,
            section,

            // internal
            _sortMs: getSortableMs(data.createdAt),
            isDiagnostic: true,
          };
        });

        // Optional enrichment from diagnostics/{id} (don’t let this break anything)
        const enriched = await Promise.all(
          base.map(async (x) => {
            try {
              const defSnap = await getDoc(doc(db, "diagnostics", x.packetId));
              if (!defSnap.exists()) return x;

              const def = defSnap.data();
              const title = def?.name || x.title;
              const count = def?.administeredCount ?? null;

              return {
                ...x,
                title,
                packetName: title,
                subtitle: `${count ?? x.subtitle?.replace?.(" Questions", "") ?? 36} Questions`,
              };
            } catch (e) {
              return x;
            }
          })
        );

        return enriched;
      } catch (err) {
        // This is likely what’s happening to you right now.
        console.warn(
          "Diagnostics not loaded (this will NOT hide assignments):",
          err
        );
        return [];
      }
    };

    const loadAll = async () => {
      const [a, d] = await Promise.all([loadAssignments(), loadDiagnostics()]);

      // If you want diagnostics mixed in by recency:
      const merged = [...d, ...a].sort((x, y) => (y._sortMs ?? 0) - (x._sortMs ?? 0));

      const cleaned = merged.map(({ _sortMs, ...rest }) => rest);

      if (!mounted) return;
      setAssignments(cleaned);
      setLoading(false);
    };

    loadAll();

    return () => {
      mounted = false;
    };
  }, [user?.uid]);

  return { assignments, loading };
}
